// Las verificaciones de MetaMap del cliente. Es la QUINTA lectura de la ficha, contra el silo del
// wizard y con otro permiso: se cae por su cuenta y no vacía a las otras cuatro.
import { useQuery } from '@tanstack/react-query';
import {
    getCustomerKycSessions,
    type CustomerKycSession,
    type CustomerKycSessionsResult,
} from '@/api/services/retail-wizard/customerKyc.client';
import { readFailureOf, type ReadFailure } from '../lib/creditCustomersErrors';

const KEY = ['credit-admin', 'customer-kyc-sessions'] as const;

const STALE_TIME_MS = 60_000;

export interface CustomerKycSessionsState {
    /** La sesión más reciente, que es la que la ficha pinta. `null` = no hay ninguna. */
    latest: CustomerKycSession | null;
    /** Cuántas sesiones tiene el cliente en este negocio. */
    count: number;
    /** El cliente tenía más sesiones que el tope: la lista está cortada y hay que decirlo. */
    truncated: boolean;
    /** `true` cuando ya se sabe que no hay negocio con el que pedirlas: la lectura ni sale. */
    hasNoScope: boolean;
    isLoading: boolean;
    failure: ReadFailure | null;
}

/**
 * `GET /backoffice/customers/{id}/kyc-sessions?value=<retailerId>` — `retail.customer.read` con
 * scope RETAILER. El negocio es obligatorio: sin él la lectura no sale y la ficha lo dice.
 * `isScopePending` = el negocio todavía se está resolviendo: se reporta cargando, no «sin negocio».
 */
export function useCustomerKycSessions(
    retailCustomerId: string | null,
    retailerId: string | null,
    isScopePending = false,
): CustomerKycSessionsState {
    const enabled = retailCustomerId !== null && retailerId !== null;
    const lacksScope = retailCustomerId !== null && retailerId === null;

    const query = useQuery({
        queryKey: [...KEY, retailCustomerId, retailerId],
        queryFn: (): Promise<CustomerKycSessionsResult> =>
            getCustomerKycSessions(retailCustomerId ?? '', retailerId ?? undefined),
        enabled,
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    return {
        // El silo las devuelve de la más reciente a la más vieja, así que la primera es la última.
        latest: query.data?.items[0] ?? null,
        count: query.data?.count ?? 0,
        truncated: query.data?.truncated ?? false,
        hasNoScope: lacksScope && !isScopePending,
        isLoading: (enabled && query.isPending) || (lacksScope && isScopePending),
        failure: readFailureOf(query.error),
    };
}
